import type {
  EnduranceExecutionAnalysis,
  RideClassificationAnalysis,
  TymewearAnalysis,
  UnifiedActivityAnalysisInput
} from "./sessionAnalysis";

export const COACH_SUMMARY_ANALYSIS_VERSION = "coach-summary.v1";

export type CoachSummaryConfidence = "high" | "medium" | "low";

export type CoachEvidenceType = "observed" | "calculated" | "inferred" | "unavailable";

export type CoachObservationDomain =
  | "ride-classification"
  | "pacing-consistency"
  | "aerobic-decoupling"
  | "durability"
  | "respiratory-execution"
  | "training-stimulus"
  | "source-quality";

export type CoachEvidence = {
  metric: string;
  value: string | number;
  unit: string | null;
  source: string;
};

export type CoachObservation = {
  id: string;
  rank: number;
  domain: CoachObservationDomain;
  headline: string;
  explanation: string;
  evidence: CoachEvidence[];
  confidence: CoachSummaryConfidence;
  evidenceType: CoachEvidenceType;
  ruleId: string;
};

export type CoachSummary = {
  overallAssessment: string;
  observations: CoachObservation[];
  coachingTakeaway: string;
  limitations: string[];
  overallConfidence: CoachSummaryConfidence;
  generatedAt: string;
  analysisVersion: string;
};

type CoachSummaryInputs = {
  input: UnifiedActivityAnalysisInput;
  rideClassification: RideClassificationAnalysis;
  enduranceExecution: EnduranceExecutionAnalysis | null;
  respiratoryAnalysis: TymewearAnalysis | null;
  analysisConfidencePercent: number;
  generatedAt: string;
};

const COACH_SUMMARY_RULES = {
  pacing: {
    highlySteadyViMax: 1.03,
    reasonablySteadyViMax: 1.06,
    stableHalfPowerDeltaPercentMax: 3,
    variableHalfPowerDeltaPercentMin: 6
  },
  decoupling: {
    veryStableAbsPercentMax: 3,
    acceptableAbsPercentMax: 5
  },
  durability: {
    stablePowerDropPercentMax: 3,
    notablePowerDropPercentMin: 5
  },
  sourceQuality: {
    highCoveragePercentMin: 85,
    mediumCoveragePercentMin: 65
  }
} as const;

function toConfidence(score: number): CoachSummaryConfidence {
  if (score >= 80) return "high";
  if (score >= 55) return "medium";
  return "low";
}

function percentChange(first: number | null, second: number | null): number | null {
  if (first === null || second === null || first === 0) return null;
  return ((second - first) / first) * 100;
}

function safeRounded(value: number | null, digits = 1): number | null {
  if (value === null || !Number.isFinite(value)) return null;
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
}

function avg(values: number[]): number {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function scoreFromCoverage(coveragePercent: number | null | undefined): number {
  if (coveragePercent === null || coveragePercent === undefined) return 50;
  if (coveragePercent >= COACH_SUMMARY_RULES.sourceQuality.highCoveragePercentMin) return 92;
  if (coveragePercent >= COACH_SUMMARY_RULES.sourceQuality.mediumCoveragePercentMin) return 72;
  return 44;
}

function mappedClassification(type: RideClassificationAnalysis["type"]): string {
  if (type === "unknown") return "unclassified";
  if (type === "sweet_spot") return "tempo";
  if (type === "sprint") return "anaerobic";
  if (type === "group_ride" || type === "race") return "mixed";
  return type;
}

function sourceAgreementScore(input: UnifiedActivityAnalysisInput): number {
  if (!input.supportingRideSources.length) return 90;
  const values = input.supportingRideSources.map(item => item.agreement.confidencePercent);
  return Math.round(avg(values));
}

function buildRideClassificationObservation(inputs: CoachSummaryInputs): CoachObservation {
  const mapped = mappedClassification(inputs.rideClassification.type);
  const evidence: CoachEvidence[] = [
    {
      metric: "Ride class",
      value: mapped,
      unit: null,
      source: "Ride classification"
    },
    {
      metric: "Classification confidence",
      value: inputs.rideClassification.confidencePercent,
      unit: "%",
      source: "Ride classification"
    }
  ];

  if (inputs.enduranceExecution?.intensityFactor !== null && inputs.enduranceExecution?.intensityFactor !== undefined) {
    evidence.push({
      metric: "Intensity factor",
      value: safeRounded(inputs.enduranceExecution.intensityFactor, 2) ?? inputs.enduranceExecution.intensityFactor,
      unit: null,
      source: "Ride analytics"
    });
  }

  return {
    id: "ride-classification",
    rank: 0,
    domain: "ride-classification",
    headline: `${mapped.charAt(0).toUpperCase()}${mapped.slice(1)} classification supported`,
    explanation: `This session is classified as ${mapped} from the available ride evidence and athlete-specific FTP snapshot.`,
    evidence,
    confidence: toConfidence(Math.min(95, Math.round((inputs.rideClassification.confidencePercent + sourceAgreementScore(inputs.input)) / 2))),
    evidenceType: "inferred",
    ruleId: "classification.existing-result"
  };
}

function buildPacingObservation(inputs: CoachSummaryInputs): CoachObservation | null {
  const endurance = inputs.enduranceExecution;
  if (!endurance) return null;

  const vi = endurance.variabilityIndex;
  const firstHalfPower = endurance.firstHalfPower;
  const secondHalfPower = endurance.secondHalfPower;
  const halfChange = percentChange(firstHalfPower, secondHalfPower);
  const absHalfChange = halfChange === null ? null : Math.abs(halfChange);

  if (vi === null || vi === undefined || firstHalfPower === null || secondHalfPower === null) return null;

  let headline = "Reasonably steady pacing";
  let explanation = `Variability index was ${vi.toFixed(2)} with consistent power across ride halves.`;

  if (vi <= COACH_SUMMARY_RULES.pacing.highlySteadyViMax) {
    headline = "Excellent pacing consistency";
    explanation = `Variability index was ${vi.toFixed(2)}, indicating highly steady power delivery throughout the session.`;
  } else if (vi > COACH_SUMMARY_RULES.pacing.reasonablySteadyViMax) {
    headline = "Variable pacing pattern";
    explanation = `Variability index was ${vi.toFixed(2)}, showing more frequent power fluctuations during the ride.`;
  }

  if (absHalfChange !== null && absHalfChange <= COACH_SUMMARY_RULES.pacing.stableHalfPowerDeltaPercentMax) {
    explanation = `${explanation} First-half and second-half average power stayed closely aligned.`;
  }

  if (absHalfChange !== null && absHalfChange >= COACH_SUMMARY_RULES.pacing.variableHalfPowerDeltaPercentMin) {
    explanation = `${explanation} Half-to-half power shifted enough to suggest late-session execution variability.`;
  }

  const evidence: CoachEvidence[] = [
    { metric: "Average power", value: safeRounded(inputs.input.canonicalPrimaryRide.averagePower, 0) ?? "Not available", unit: "W", source: "Primary ride" },
    { metric: "Normalized power", value: safeRounded(endurance.normalizedPower, 0) ?? "Not available", unit: "W", source: "Ride analytics" },
    { metric: "Variability index", value: safeRounded(vi, 2) ?? vi, unit: null, source: "Ride analytics" },
    { metric: "First-half power", value: safeRounded(firstHalfPower, 0) ?? firstHalfPower, unit: "W", source: "Ride analytics" },
    { metric: "Second-half power", value: safeRounded(secondHalfPower, 0) ?? secondHalfPower, unit: "W", source: "Ride analytics" },
    { metric: "Coasting time", value: safeRounded(endurance.coastingTimeSeconds / 60, 1) ?? 0, unit: "min", source: "Ride analytics" },
    { metric: "Stop time", value: safeRounded(endurance.stopTimeSeconds / 60, 1) ?? 0, unit: "min", source: "Ride analytics" }
  ];

  const coverageScore = scoreFromCoverage(inputs.input.canonicalPrimaryRide.rideAnalytics?.validPowerDurationSeconds && inputs.input.canonicalPrimaryRide.movingTime
    ? (inputs.input.canonicalPrimaryRide.rideAnalytics.validPowerDurationSeconds / inputs.input.canonicalPrimaryRide.movingTime) * 100
    : null);
  const variabilityScore = vi <= COACH_SUMMARY_RULES.pacing.highlySteadyViMax
    ? 92
    : vi <= COACH_SUMMARY_RULES.pacing.reasonablySteadyViMax
      ? 78
      : 60;
  const contradictionPenalty = absHalfChange !== null && absHalfChange >= COACH_SUMMARY_RULES.pacing.variableHalfPowerDeltaPercentMin ? 14 : 0;

  return {
    id: "pacing-consistency",
    rank: 0,
    domain: "pacing-consistency",
    headline,
    explanation,
    evidence,
    confidence: toConfidence(Math.max(20, Math.round((coverageScore + variabilityScore) / 2) - contradictionPenalty)),
    evidenceType: "calculated",
    ruleId: "pacing.vi-and-half-power"
  };
}

function buildDecouplingObservation(inputs: CoachSummaryInputs): CoachObservation | null {
  const endurance = inputs.enduranceExecution;
  const decoupling = endurance?.aerobicDecouplingPercent;
  const rideDecoupling = inputs.input.canonicalPrimaryRide.rideAnalytics?.aerobicDecoupling;
  if (decoupling === null || decoupling === undefined || !rideDecoupling) return null;

  const absDecoupling = Math.abs(decoupling);
  let headline = "Notable aerobic drift observed";
  let explanation = `Aerobic decoupling was ${decoupling.toFixed(1)}%, indicating measurable drift across the session.`;

  if (absDecoupling <= COACH_SUMMARY_RULES.decoupling.veryStableAbsPercentMax) {
    headline = "Very stable aerobic coupling";
    explanation = `Aerobic decoupling was ${decoupling.toFixed(1)}%, which is very stable for this session.`;
  } else if (absDecoupling <= COACH_SUMMARY_RULES.decoupling.acceptableAbsPercentMax) {
    headline = "Acceptable aerobic drift";
    explanation = `Aerobic decoupling was ${decoupling.toFixed(1)}%, within an acceptable session range.`;
  }

  if (decoupling < 0) {
    explanation = `${explanation} Negative drift can reflect slightly improved efficiency during this ride and may include warm-up influence.`;
  }

  const coveragePercent = rideDecoupling.coveragePercent;
  const evidence: CoachEvidence[] = [
    { metric: "Aerobic decoupling", value: safeRounded(decoupling, 1) ?? decoupling, unit: "%", source: "Ride analytics" },
    { metric: "Valid-duration coverage", value: safeRounded(coveragePercent, 0) ?? "Not available", unit: "%", source: "Ride analytics" },
    { metric: "First-half efficiency factor", value: safeRounded(rideDecoupling.firstHalfEfficiencyFactor, 2) ?? "Not available", unit: null, source: "Ride analytics" },
    { metric: "Second-half efficiency factor", value: safeRounded(rideDecoupling.secondHalfEfficiencyFactor, 2) ?? "Not available", unit: null, source: "Ride analytics" }
  ];

  const statusPenalty = rideDecoupling.status === "available" ? 0 : 30;
  const confidenceScore = Math.max(20, scoreFromCoverage(coveragePercent) - statusPenalty);

  return {
    id: "aerobic-decoupling",
    rank: 0,
    domain: "aerobic-decoupling",
    headline,
    explanation,
    evidence,
    confidence: toConfidence(confidenceScore),
    evidenceType: "calculated",
    ruleId: "decoupling.absolute-percent"
  };
}

function buildDurabilityObservation(inputs: CoachSummaryInputs): CoachObservation | null {
  const endurance = inputs.enduranceExecution;
  if (!endurance) return null;
  const firstHalfPower = endurance.firstHalfPower;
  const secondHalfPower = endurance.secondHalfPower;
  const powerChangePercent = percentChange(firstHalfPower, secondHalfPower);
  if (firstHalfPower === null || secondHalfPower === null || powerChangePercent === null) return null;

  const absPowerChange = Math.abs(powerChangePercent);
  let headline = "Stable durability profile";
  let explanation = `Second-half average power changed by ${safeRounded(powerChangePercent, 1)}% versus the first half, indicating stable late-session durability.`;

  if (absPowerChange >= COACH_SUMMARY_RULES.durability.notablePowerDropPercentMin && powerChangePercent < 0) {
    headline = "Late-session fade observed";
    explanation = `Second-half average power was ${safeRounded(absPowerChange, 1)}% lower than the first half, indicating meaningful fade in this session.`;
  } else if (absPowerChange > COACH_SUMMARY_RULES.durability.stablePowerDropPercentMax) {
    explanation = `Second-half average power shifted by ${safeRounded(powerChangePercent, 1)}% versus the first half, showing moderate durability variation within this ride.`;
  }

  const durability60 = endurance.durability.find(item => item.afterMinutes === 60)?.powerChangePercent ?? null;
  const durability90 = endurance.durability.find(item => item.afterMinutes === 90)?.powerChangePercent ?? null;
  const hrChange = percentChange(endurance.firstHalfHeartRate, endurance.secondHalfHeartRate);

  const evidence: CoachEvidence[] = [
    { metric: "First-half power", value: safeRounded(firstHalfPower, 0) ?? firstHalfPower, unit: "W", source: "Ride analytics" },
    { metric: "Second-half power", value: safeRounded(secondHalfPower, 0) ?? secondHalfPower, unit: "W", source: "Ride analytics" },
    { metric: "Power change", value: safeRounded(powerChangePercent, 1) ?? powerChangePercent, unit: "%", source: "Ride analytics" },
    { metric: "Durability after 60 min", value: safeRounded(durability60, 1) ?? "Not available", unit: "%", source: "Ride analytics" },
    { metric: "Durability after 90 min", value: safeRounded(durability90, 1) ?? "Not available", unit: "%", source: "Ride analytics" },
    { metric: "Heart-rate half change", value: safeRounded(hrChange, 1) ?? "Not available", unit: "%", source: "Ride analytics" }
  ];

  const availableDurabilityMetrics = [durability60, durability90].filter((value): value is number => value !== null).length;
  const completenessScore = 60 + availableDurabilityMetrics * 15 + (hrChange !== null ? 10 : 0);

  return {
    id: "durability",
    rank: 0,
    domain: "durability",
    headline,
    explanation,
    evidence,
    confidence: toConfidence(completenessScore),
    evidenceType: "calculated",
    ruleId: "durability.half-power-and-checkpoints"
  };
}

function buildRespiratoryObservation(inputs: CoachSummaryInputs): CoachObservation | null {
  const respiratory = inputs.respiratoryAnalysis;
  if (!respiratory) return null;

  const ventilation = respiratory.metrics.minuteVentilation;
  const breathing = respiratory.metrics.breathingFrequency;
  const tidal = respiratory.metrics.tidalVolume;
  if (!ventilation && !breathing && !tidal) return null;

  let headline = "Respiratory execution captured";
  let explanation = "Respiratory demand was trackable across the session.";

  const drift = ventilation?.driftPercent ?? null;
  if (drift !== null) {
    if (Math.abs(drift) <= 3) {
      explanation = "Minute ventilation stayed stable across ride halves.";
    } else if (drift > 3) {
      explanation = "Minute ventilation increased across ride halves.";
    } else {
      explanation = "Minute ventilation decreased across ride halves.";
    }
  }

  const correlation = respiratory.ventilationToPowerCorrelation;
  if (correlation !== null) {
    if (correlation >= 0.7) {
      explanation = `${explanation} Ventilation closely tracked power.`;
      headline = "Stable respiratory-power coupling";
    } else if (correlation <= 0.4) {
      explanation = `${explanation} Ventilation weakly tracked power.`;
      headline = "Respiratory-power coupling was weak";
    } else {
      explanation = `${explanation} Ventilation tracked power with moderate consistency.`;
    }
  }

  const evidence: CoachEvidence[] = [
    { metric: "Average minute ventilation", value: safeRounded(ventilation?.average ?? null, 1) ?? "Not available", unit: "L/min", source: "Tymewear" },
    { metric: "Maximum minute ventilation", value: safeRounded(ventilation?.maximum ?? null, 1) ?? "Not available", unit: "L/min", source: "Tymewear" },
    { metric: "Average breathing frequency", value: safeRounded(breathing?.average ?? null, 1) ?? "Not available", unit: "breaths/min", source: "Tymewear" },
    { metric: "Maximum breathing frequency", value: safeRounded(breathing?.maximum ?? null, 1) ?? "Not available", unit: "breaths/min", source: "Tymewear" },
    { metric: "Average tidal volume", value: safeRounded(tidal?.average ?? null, 2) ?? "Not available", unit: "L", source: "Tymewear" },
    { metric: "Respiratory drift", value: safeRounded(drift, 1) ?? "Not available", unit: "%", source: "Tymewear" },
    { metric: "Ventilation first-half", value: safeRounded(ventilation?.firstHalfAverage ?? null, 1) ?? "Not available", unit: "L/min", source: "Tymewear" },
    { metric: "Ventilation second-half", value: safeRounded(ventilation?.secondHalfAverage ?? null, 1) ?? "Not available", unit: "L/min", source: "Tymewear" },
    { metric: "Ventilation-power correlation", value: safeRounded(correlation, 2) ?? "Not available", unit: null, source: "Derived from Tymewear + power" },
    { metric: "Ride coverage", value: respiratory.dataCoveragePercent, unit: "%", source: "Tymewear alignment" },
    { metric: "Alignment confidence", value: respiratory.alignmentConfidence, unit: null, source: "Tymewear alignment" }
  ];

  const alignmentScore = respiratory.alignmentConfidence === "high" ? 90 : respiratory.alignmentConfidence === "medium" ? 72 : 48;
  const coverageScore = scoreFromCoverage(respiratory.dataCoveragePercent);
  const usableScore = respiratory.aligned ? 90 : 58;

  return {
    id: "respiratory-execution",
    rank: 0,
    domain: "respiratory-execution",
    headline,
    explanation,
    evidence,
    confidence: toConfidence(Math.round((alignmentScore + coverageScore + usableScore) / 3)),
    evidenceType: "observed",
    ruleId: "respiratory.aligned-tymewear"
  };
}

function buildTrainingStimulusObservation(inputs: CoachSummaryInputs): CoachObservation {
  const endurance = inputs.enduranceExecution;
  const hasPrescription = inputs.input.strengthData.length > 0;
  const ride = inputs.input.canonicalPrimaryRide;
  const zoneSummary = ride.rideAnalytics?.powerZones
    .filter(zone => (zone.percentageOfMovingTime ?? 0) > 0)
    .map(zone => `${zone.zoneName} ${(zone.percentageOfMovingTime ?? 0).toFixed(0)}%`)
    .slice(0, 3)
    .join(", ");

  const headline = hasPrescription
    ? "Observed execution with attached prescription"
    : "Observed execution without attached prescription";

  const explanation = hasPrescription
    ? "A structured target source was attached; this summary reports observed output from this single session only."
    : "No prescribed target was attached, so this summary reflects observed execution from the recorded ride only.";

  const evidence: CoachEvidence[] = [
    { metric: "FTP snapshot", value: inputs.input.ftpWatts, unit: "W", source: "Athlete profile snapshot" },
    { metric: "Intensity factor", value: safeRounded(endurance?.intensityFactor ?? null, 2) ?? "Not available", unit: null, source: "Ride analytics" },
    { metric: "TSS", value: safeRounded(ride.rideAnalytics?.trainingStressScore ?? null, 0) ?? "Not available", unit: null, source: "Ride analytics" },
    { metric: "Duration", value: safeRounded((ride.elapsedTime ?? ride.movingTime ?? 0) / 60, 1) ?? 0, unit: "min", source: "Primary ride" },
    { metric: "Ride class", value: mappedClassification(inputs.rideClassification.type), unit: null, source: "Ride classification" },
    { metric: "Power-zone distribution", value: zoneSummary || "Not available", unit: null, source: "Ride analytics" },
    { metric: "Prescription attached", value: hasPrescription ? "yes" : "no", unit: null, source: "Attached sources" }
  ];

  const confidenceScore = hasPrescription ? 78 : 66;

  return {
    id: "training-stimulus",
    rank: 0,
    domain: "training-stimulus",
    headline,
    explanation,
    evidence,
    confidence: toConfidence(confidenceScore),
    evidenceType: "observed",
    ruleId: "stimulus.ftp-if-tss-zones"
  };
}

function buildSourceQualityObservation(inputs: CoachSummaryInputs): CoachObservation {
  const sourceAgreement = sourceAgreementScore(inputs.input);
  const respiratory = inputs.respiratoryAnalysis;
  const respiratoryAlignmentScore = respiratory
    ? respiratory.alignmentConfidence === "high"
      ? 92
      : respiratory.alignmentConfidence === "medium"
        ? 72
        : 45
    : 60;
  const completeness = inputs.input.dataCoverage.activityCompletenessPercent;

  const qualityScore = Math.round((sourceAgreement + respiratoryAlignmentScore + completeness) / 3);

  const evidence: CoachEvidence[] = [
    { metric: "Source agreement", value: sourceAgreement, unit: "%", source: "Supporting ride agreement" },
    { metric: "Activity completeness", value: completeness, unit: "%", source: "Data coverage" },
    { metric: "Respiratory alignment", value: respiratory?.alignmentConfidence ?? "Not available", unit: null, source: "Tymewear alignment" }
  ];

  return {
    id: "source-quality",
    rank: 0,
    domain: "source-quality",
    headline: qualityScore >= 80 ? "Evidence quality is strong" : qualityScore >= 55 ? "Evidence quality is usable" : "Evidence quality is limited",
    explanation: "Summary confidence reflects source agreement, coverage, and alignment quality across attached data.",
    evidence,
    confidence: toConfidence(qualityScore),
    evidenceType: "calculated",
    ruleId: "sources.coverage-agreement-alignment"
  };
}

function summarizeOverallAssessment(observations: CoachObservation[]): string {
  const byDomain = new Map(observations.map(item => [item.domain, item]));
  const classification = byDomain.get("ride-classification");
  const pacing = byDomain.get("pacing-consistency");
  const decoupling = byDomain.get("aerobic-decoupling");
  const respiratory = byDomain.get("respiratory-execution");

  const clauses = [
    classification ? classification.headline.toLowerCase() : "session evidence summarized",
    pacing ? pacing.headline.toLowerCase() : null,
    decoupling ? decoupling.headline.toLowerCase() : null,
    respiratory ? respiratory.headline.toLowerCase() : null
  ].filter((value): value is string => value !== null);

  if (clauses.length === 0) {
    return "Session evidence was summarized, but limited metrics reduced interpretive detail.";
  }

  const primary = clauses.slice(0, 3).join(", ");
  return `${primary.charAt(0).toUpperCase()}${primary.slice(1)}.`;
}

function summarizeTakeaway(observations: CoachObservation[], hasPrescription: boolean): string {
  const pacing = observations.find(item => item.domain === "pacing-consistency");
  const durability = observations.find(item => item.domain === "durability");
  const decoupling = observations.find(item => item.domain === "aerobic-decoupling");

  if (pacing && durability && decoupling) {
    if (hasPrescription) {
      return "This session delivered the planned workload pattern with stable execution and limited late-session fade.";
    }
    return "This session showed a clear workload pattern with stable execution and limited late-session fade.";
  }

  if (pacing && decoupling) {
    if (hasPrescription) {
      return "Execution remained controlled through the ride, supporting the prescribed session target.";
    }
    return "Execution remained controlled through the ride, supporting a repeatable stimulus in this single session.";
  }

  return "Use this session as a single-ride benchmark and compare against future rides only when equivalent evidence is available.";
}

function buildLimitations(inputs: CoachSummaryInputs): string[] {
  const limitations: string[] = [];
  if (!inputs.input.hydrationSources.length) {
    limitations.push("Hydration analysis was unavailable because no Nix source was attached.");
  }
  if (!inputs.input.strengthData.length) {
    limitations.push("No prescribed workout target was attached, so conclusions are observational.");
  }
  limitations.push("Historical trend comparison was unavailable; this summary reflects only the current session.");

  if (!inputs.respiratoryAnalysis) {
    limitations.push("Respiratory analysis was unavailable because no aligned Tymewear dataset was attached.");
  } else if (!inputs.respiratoryAnalysis.aligned || inputs.respiratoryAnalysis.dataCoveragePercent < 50) {
    limitations.push("Respiratory confidence is reduced because Tymewear alignment or coverage was limited.");
  }

  const decouplingCoverage = inputs.input.canonicalPrimaryRide.rideAnalytics?.aerobicDecoupling.coveragePercent;
  if (decouplingCoverage !== null && decouplingCoverage !== undefined && decouplingCoverage < 70) {
    limitations.push("Aerobic decoupling confidence is reduced due to limited valid-duration coverage.");
  }

  return limitations;
}

export function buildCoachSummary(inputs: CoachSummaryInputs): CoachSummary {
  const hasPrescription = inputs.input.strengthData.length > 0;
  const observations = [
    buildRideClassificationObservation(inputs),
    buildPacingObservation(inputs),
    buildDecouplingObservation(inputs),
    buildDurabilityObservation(inputs),
    buildRespiratoryObservation(inputs),
    buildTrainingStimulusObservation(inputs),
    buildSourceQualityObservation(inputs)
  ].filter((item): item is CoachObservation => item !== null);

  const ranked = observations
    .sort((left, right) => {
      const confidenceWeight = { high: 3, medium: 2, low: 1 } as const;
      const confidenceDelta = confidenceWeight[right.confidence] - confidenceWeight[left.confidence];
      if (confidenceDelta !== 0) return confidenceDelta;
      return left.id.localeCompare(right.id);
    })
    .slice(0, 5)
    .map((item, index) => ({ ...item, rank: index + 1 }));

  const withMinimum = ranked.length >= 3
    ? ranked
    : observations
      .slice(0, 3)
      .map((item, index) => ({ ...item, rank: index + 1 }));

  const confidenceScores: number[] = withMinimum.map(item => item.confidence === "high" ? 90 : item.confidence === "medium" ? 68 : 42);
  confidenceScores.push(inputs.analysisConfidencePercent);

  return {
    overallAssessment: summarizeOverallAssessment(withMinimum),
    observations: withMinimum,
    coachingTakeaway: summarizeTakeaway(withMinimum, hasPrescription),
    limitations: buildLimitations(inputs),
    overallConfidence: toConfidence(Math.round(avg(confidenceScores))),
    generatedAt: inputs.generatedAt,
    analysisVersion: COACH_SUMMARY_ANALYSIS_VERSION
  };
}
